import type { Task } from "@app/core";
import { useEffect, useRef, useState } from "react";
import { useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { isCalendarBackgroundCreateSuppressed } from "./calendar-background-create-guard.js";
import { blockDragState, getDragTaskId, occurrenceIsoOf, rescheduleTo, splitVirtualId } from "./drag.js";
import { dayStart } from "./month-helpers.js";
import { resolveSource } from "./resolve.js";
import { useRecurringTaskActions } from "./useRecurringTaskActions.js";
import { useWindowMouseUp } from "./useWindowMouseUp.js";

export interface ColInfo { date: Date; tasks: Task[] }
export interface AllDayTask { task: Task; startCol: number; endCol: number }

const ALL_DAY_MIN = 1440;

type AllDayOp =
  | { kind: "create"; startCol: number; endCol: number }
  | { kind: "move"; task: Task; spanCols: number; startCol: number; currentCol: number }
  | { kind: "resize-left"; task: Task; taskEndCol: number; currentCol: number }
  | { kind: "resize-right"; task: Task; taskStartCol: number; currentCol: number };

/**
 * Column-based drag (create / move / resize) for the week/day all-day row.
 * Motion is tracked by `onColEnter` (called from each cell's onMouseEnter); the
 * commit happens on a one-shot window mouseup. Recurring moves go through the
 * shared `useRecurringTaskActions`, matching the timed-grid and month flows.
 */
export function useAllDayDrag(days: ColInfo[], allDayTaskMap: Map<string, AllDayTask>) {
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const addTask = useTasksStore((s) => s.add);
  const actions = useRecurringTaskActions();
  const onMouseUpOnce = useWindowMouseUp();

  const [op, setOp] = useState<AllDayOp | null>(null);
  const [nativeDropCol, setNativeDropCol] = useState<number | null>(null);
  const opRef = useRef<AllDayOp | null>(null);
  function setOpState(next: AllDayOp | null) { opRef.current = next; setOp(next); }

  useEffect(() => {
    // On any drag end/drop, clear both the native drop indicator and any stale
    // mouse-based op (startMove registers a one-shot mouseup listener that never
    // fires during native drag, leaving opRef stuck until cleared here).
    const clear = () => { setNativeDropCol(null); setOpState(null); };
    window.addEventListener("dragend", clear);
    window.addEventListener("drop", clear, true);
    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("drop", clear, true);
    };
  }, []);

  function allDayDuration(task: Task | null | undefined): number {
    const minutes = Math.max(0, task?.duration_minutes ?? 0);
    return Math.max(ALL_DAY_MIN, Math.ceil(minutes / ALL_DAY_MIN) * ALL_DAY_MIN || ALL_DAY_MIN);
  }

  function startCreate(e: React.MouseEvent, col: number) {
    if (e.button !== 0) return;
    if (isCalendarBackgroundCreateSuppressed() || useTaskDialogStore.getState().open) return;
    e.preventDefault();
    setOpState({ kind: "create", startCol: col, endCol: col });
    onMouseUpOnce(() => {
      const d = opRef.current;
      setOpState(null);
      if (!d || d.kind !== "create") return;
      const lo = Math.min(d.startCol, d.endCol);
      const hi = Math.max(d.startCol, d.endCol);
      const loDay = days[lo]?.date;
      if (!loDay) return;
      const spanCount = hi - lo + 1;
      if (spanCount === 1) { openCreate(loDay, 1440); }
      else { void addTask({ title: "", due_at: loDay.toISOString(), all_day: true, duration_minutes: spanCount * 1440 }).then((t) => openEdit(t)); }
    });
  }

  function startMove(e: React.MouseEvent, task: Task, col: number) {
    e.preventDefault();
    e.stopPropagation();
    if (e.button !== 0) return;
    const span = allDayTaskMap.get(task.id);
    const spanCols = span ? span.endCol - span.startCol + 1 : 1;
    setOpState({ kind: "move", task, spanCols, startCol: col, currentCol: col });
    onMouseUpOnce(() => {
      const d = opRef.current;
      setOpState(null);
      if (!d || d.kind !== "move" || !d.task.due_at) return;
      const span2 = allDayTaskMap.get(d.task.id);
      const originalStartCol = span2 ? span2.startCol : 0;
      const delta = d.currentCol - d.startCol;
      if (delta === 0) return;
      const newStartCol = Math.max(0, Math.min(days.length - d.spanCols, originalStartCol + delta));
      const newDay = days[newStartCol]?.date;
      if (!newDay) return;
      // Read tasks LIVE at commit time (not a subscribed slice): AllDayRow is
      // always mounted, so subscribing to the whole tasks array re-rendered it on
      // every edit. Matches useMonthDrag's getState() pattern.
      const source = resolveSource(d.task, useTasksStore.getState().tasks);
      // The dragged occurrence (not the series' base/dtstart) is what moves.
      const draggedOcc = occurrenceIsoOf(d.task);
      const newIso = rescheduleTo(draggedOcc, newDay);
      void actions.rescheduleTask({ source, oldOcc: draggedOcc, newIso, isVirtual: !!d.task.virtual });
    });
  }

  function startResizeLeft(e: React.MouseEvent, task: Task) {
    e.preventDefault();
    e.stopPropagation();
    if (e.button !== 0) return;
    const span = allDayTaskMap.get(task.id);
    setOpState({ kind: "resize-left", task, taskEndCol: span ? span.endCol : 0, currentCol: span ? span.startCol : 0 });
    onMouseUpOnce(() => {
      const d = opRef.current;
      setOpState(null);
      if (!d || d.kind !== "resize-left" || !d.task.due_at) return;
      const newStartCol = Math.min(d.currentCol, d.taskEndCol);
      const origSpan = allDayTaskMap.get(d.task.id);
      if (origSpan && newStartCol === origSpan.startCol) return; // unchanged
      const newDay = days[newStartCol]?.date;
      if (!newDay) return;
      const nDays = d.taskEndCol - newStartCol + 1;
      // Route through the shared recurring dispatch (like startMove): editing a
      // virtual occurrence's id directly would find nothing in the store and
      // silently no-op. Non-recurring falls through to the same plain edit.
      const source = resolveSource(d.task, useTasksStore.getState().tasks);
      const occKey = occurrenceIsoOf(d.task);
      if (!occKey) return;
      void actions.resizeTaskFromTop({ source, occKey, newStartIso: dayStart(newDay).toISOString(), newMinutes: nDays * 1440, isVirtual: !!d.task.virtual });
    });
  }

  function startResizeRight(e: React.MouseEvent, task: Task) {
    e.preventDefault();
    e.stopPropagation();
    if (e.button !== 0) return;
    const span = allDayTaskMap.get(task.id);
    setOpState({ kind: "resize-right", task, taskStartCol: span ? span.startCol : 0, currentCol: span ? span.endCol : 0 });
    onMouseUpOnce(() => {
      const d = opRef.current;
      setOpState(null);
      if (!d || d.kind !== "resize-right" || !d.task.due_at) return;
      const newEndCol = Math.max(d.currentCol, d.taskStartCol);
      const origSpan = allDayTaskMap.get(d.task.id);
      if (origSpan && newEndCol === origSpan.endCol) return; // unchanged
      const nDays = newEndCol - d.taskStartCol + 1;
      const source = resolveSource(d.task, useTasksStore.getState().tasks);
      void actions.resizeTask({ source, occKey: d.task.due_at, newMinutes: nDays * 1440, isVirtual: !!d.task.virtual });
    });
  }

  function onColEnter(col: number) {
    const cur = opRef.current;
    if (!cur) return;
    if (cur.kind === "create") setOpState({ ...cur, endCol: col });
    else setOpState({ ...cur, currentCol: col });
  }

  function onNativeDragOver(e: React.DragEvent, col: number) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    setNativeDropCol(col);
  }

  function onNativeDragLeave(e: React.DragEvent, col: number) {
    const next = e.relatedTarget;
    if (next instanceof Node && e.currentTarget.contains(next)) return;
    setNativeDropCol((cur) => (cur === col ? null : cur));
  }

  async function onNativeDrop(e: React.DragEvent, col: number) {
    e.preventDefault();
    e.stopPropagation();
    setNativeDropCol(null);

    const draggedId = getDragTaskId(e);
    const targetDay = days[col]?.date;
    if (!draggedId || !targetDay) return;

    const { sourceId, occIso } = splitVirtualId(draggedId);
    const source = useTasksStore.getState().tasks.find((t) => t.id === sourceId);
    if (!source) return;

    const draggedTask = blockDragState()?.visual.task ?? null;
    const oldOcc = occIso ?? source.due_at;
    // An already-all-day chip dropped within the row is a plain MOVE, not a
    // conversion: keep its duration and use the "reschedule" confirm (the
    // convert wording/rounding is for timed blocks dropped onto the band).
    // Back on its own day = no-op — skip the edit AND the recurring confirm
    // (mirrors the timed-grid and month guards); compare against the dragged
    // chip's DISPLAYED start (an overridden occurrence renders away from its
    // RECURRENCE-ID slot).
    if (source.all_day) {
      const displayedIso = draggedTask?.due_at ?? oldOcc;
      if (displayedIso && dayStart(new Date(displayedIso)).getTime() === dayStart(targetDay).getTime()) return;
      await actions.rescheduleTask({ source, oldOcc, newIso: rescheduleTo(oldOcc, dayStart(targetDay)), isVirtual: occIso !== null });
      return;
    }
    await actions.convertTaskToAllDay({
      source,
      oldOcc,
      newIso: dayStart(targetDay).toISOString(),
      durationMinutes: allDayDuration(draggedTask ?? source),
      isVirtual: occIso !== null,
    });
  }

  function isHighlighted(col: number): boolean {
    // Native drag (from any source) takes priority — mouse-based op may still be
    // set if a drag started before mouseup could clear it.
    if (nativeDropCol !== null) return col === nativeDropCol;
    if (!op) return false;
    if (op.kind === "create") {
      return col >= Math.min(op.startCol, op.endCol) && col <= Math.max(op.startCol, op.endCol);
    }
    if (op.kind === "move") {
      const span = allDayTaskMap.get(op.task.id);
      const origStart = span ? span.startCol : 0;
      const newStart = Math.max(0, Math.min(days.length - op.spanCols, origStart + (op.currentCol - op.startCol)));
      return col >= newStart && col < newStart + op.spanCols;
    }
    if (op.kind === "resize-left") {
      return col >= Math.min(op.currentCol, op.taskEndCol) && col <= op.taskEndCol;
    }
    return col >= op.taskStartCol && col <= Math.max(op.currentCol, op.taskStartCol);
  }

  return {
    startCreate,
    startMove,
    startResizeLeft,
    startResizeRight,
    onColEnter,
    onNativeDragOver,
    onNativeDragLeave,
    onNativeDrop,
    isHighlighted,
  };
}
